import React, { useState, useEffect, useCallback } from "react";
import {
  VStack,
  HStack,
  Text,
  Card,
  Progress,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
} from "@chakra-ui/react";
import {
  FaChartBar,
  FaUsers,
  FaFileAlt,
  FaAsterisk,
  FaSyncAlt,
  FaComments,
  FaReply,
  FaClock,
  FaArrowUp,
} from "react-icons/fa";
import axiosInstance from "../../../routes/axiosInstance";

const ReportTab = ({ space, currentUser, isActive }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasDataLoaded, setHasDataLoaded] = useState(false);

  const fetchDetailedReport = useCallback(async () => {
    if (!space?._id) return;

    setLoading(true);
    setError(null);
    try {
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${currentUser.token}`,
        },
      };
      const response = await axiosInstance.get(
        `/api/spaces/${space._id}/report`,
        config,
      );
      setReportData(response.data.data);
      setHasDataLoaded(true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch detailed report");
    } finally {
      setLoading(false);
    }
  }, [space?._id, currentUser.token]);

  // Fetch report data only when the tab becomes active and data hasn't been loaded
  useEffect(() => {
    if (isActive && space?._id && !hasDataLoaded) {
      fetchDetailedReport();
    }
  }, [isActive, space?._id, hasDataLoaded, fetchDetailedReport]);

  // Refresh data function for manual refresh
  const handleRefresh = () => {
    setHasDataLoaded(false); // Reset the flag to force fresh data
    fetchDetailedReport();
  };

  // Show a message when tab is not active yet
  if (!isActive) {
    return (
      <Card p={8} borderRadius="xl" boxShadow="md" textAlign="center">
        <Text color="gray.500" fontSize="lg">
          Click the Report tab to load analytics data
        </Text>
        <Text color="gray.400" fontSize="sm" mt={2}>
          Report data will be fetched when you activate this tab
        </Text>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card p={8} borderRadius="xl" boxShadow="md" textAlign="center">
        <Spinner size="xl" color="teal.500" />
        <Text mt={4} color="gray.600">
          Loading detailed report...
        </Text>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="xl">
        <AlertIcon />
        <AlertTitle>Report Error!</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Display report based on available data
  if (!reportData) {
    return (
      <Card p={8} borderRadius="xl" boxShadow="md" textAlign="center">
        <Text color="gray.500" fontSize="lg">
          No report data available
        </Text>
        <Text color="gray.400" fontSize="sm" mt={2}>
          Reports will appear here once data is available
        </Text>
      </Card>
    );
  }

  // Handle different space types with unified report data
  if (space?.kind === "FORM") {
    return (
      <DetailedFormReport
        reportData={reportData}
        onRefresh={handleRefresh}
        loading={loading}
      />
    );
  } else if (space?.kind === "POLL") {
    return (
      <PollReportDetailed
        reportData={reportData}
        onRefresh={handleRefresh}
        loading={loading}
      />
    );
  } else if (space?.kind === "BROADCAST") {
    return (
      <BroadcastReportDetailed
        reportData={reportData}
        onRefresh={handleRefresh}
        loading={loading}
      />
    );
  } else if (space?.kind === "CHAT") {
    return (
      <ChatReportDetailed
        reportData={reportData}
        onRefresh={handleRefresh}
        loading={loading}
      />
    );
  }

  // Fallback for other space types
  return <BasicReport reportData={reportData} />;
};

const BasicReport = ({ reportData }) => {
  if (reportData.message) {
    return (
      <Card p={8} borderRadius="xl" boxShadow="md" textAlign="center">
        <Text color="gray.500" fontSize="lg">
          {reportData.message}
        </Text>
        <Text color="gray.400" fontSize="sm" mt={2}>
          More detailed reports coming soon for this space type
        </Text>
      </Card>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Card p={4} borderRadius="xl" boxShadow="md">
        <HStack justify="space-between" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="teal.600">
            Activity Report
          </Text>
          <Badge
            colorScheme="teal"
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
          >
            {reportData.spaceInfo?.kind}
          </Badge>
        </HStack>
        <Text>Basic report for {reportData.spaceInfo?.title}</Text>
      </Card>
    </VStack>
  );
};

const PollReportDetailed = ({ reportData, onRefresh, loading }) => {
  const { summary, voteAnalysis, votes } = reportData;

  return (
    <VStack align="stretch" spacing={6}>
      {/* Summary Stats */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <HStack justify="space-between" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="teal.600">
            Poll Report
          </Text>
          <Button
            size="sm"
            colorScheme="teal"
            variant="outline"
            borderRadius="md"
            onClick={onRefresh}
            isLoading={loading}
          >
            <FaSyncAlt />
          </Button>
        </HStack>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Stat>
            <StatLabel>
              <FaChartBar style={{ display: "inline", marginRight: "8px" }} />
              Total Votes
            </StatLabel>
            <StatNumber color="teal.600">{summary.totalVotes}</StatNumber>
            <StatHelpText>All participants</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>
              <FaUsers style={{ display: "inline", marginRight: "8px" }} />
              Members
            </StatLabel>
            <StatNumber color="blue.600">
              {summary.totalParticipants}
            </StatNumber>
            <StatHelpText>Total participants</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Response Rate</StatLabel>
            <StatNumber color="green.600">{summary.responseRate}</StatNumber>
            <StatHelpText>Completion rate</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Last Vote</StatLabel>
            <StatNumber fontSize="sm" color="gray.600">
              {summary.lastVoteAt
                ? new Date(summary.lastVoteAt).toLocaleDateString()
                : "None"}
            </StatNumber>
            <StatHelpText>Most recent</StatHelpText>
          </Stat>
        </SimpleGrid>
      </Card>

      {/* Vote Analysis */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Vote Distribution
        </Text>

        <VStack align="stretch" spacing={3}>
          {voteAnalysis.map((option) => (
            <Card key={option.optionId} p={4} borderRadius="lg" bg="gray.50">
              <HStack justify="space-between" mb={2}>
                <Text fontWeight="semibold">{option.optionLabel}</Text>
                <Badge colorScheme="blue" borderRadius="full">
                  {option.count} vote{option.count !== 1 ? "s" : ""} (
                  {option.percentage}%)
                </Badge>
              </HStack>
              <Progress
                value={parseFloat(option.percentage)}
                colorScheme="blue"
                borderRadius="full"
                bg="gray.200"
              />
            </Card>
          ))}
        </VStack>
      </Card>

      {/* Recent Votes */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Recent Votes ({votes.length})
        </Text>

        <VStack align="stretch" spacing={3} maxH="400px" overflowY="auto">
          {votes.slice(0, 10).map((vote, index) => (
            <Card key={vote.id} p={4} borderRadius="lg" bg="gray.50">
              <HStack justify="space-between" mb={2}>
                <HStack>
                  <Badge colorScheme="teal" borderRadius="full">
                    #{index + 1}
                  </Badge>
                  <Text fontWeight="semibold">{vote.member.alias}</Text>
                  {vote.member.isAnonymous && (
                    <Badge colorScheme="orange" size="sm">
                      Anonymous
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  {new Date(vote.votedAt).toLocaleDateString()}
                </Text>
              </HStack>
              <Text fontSize="sm" color="gray.700">
                Voted for option: {vote.pollOptionId}
              </Text>
            </Card>
          ))}
        </VStack>
      </Card>
    </VStack>
  );
};

const BroadcastReportDetailed = ({ reportData, onRefresh, loading }) => {
  const { summary, reactionAnalysis, reactions } = reportData;

  return (
    <VStack align="stretch" spacing={6}>
      {/* Summary Stats */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <HStack justify="space-between" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="teal.600">
            Broadcast Report
          </Text>
          <Button
            size="sm"
            colorScheme="teal"
            variant="outline"
            borderRadius="md"
            onClick={onRefresh}
            isLoading={loading}
          >
            <FaSyncAlt />
          </Button>
        </HStack>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Stat>
            <StatLabel>Total Reactions</StatLabel>
            <StatNumber color="teal.600">{summary.totalReactions}</StatNumber>
            <StatHelpText>All reactions</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Unique Reactions</StatLabel>
            <StatNumber color="blue.600">{summary.uniqueReactions}</StatNumber>
            <StatHelpText>Different emojis</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>
              <FaUsers style={{ display: "inline", marginRight: "8px" }} />
              Members
            </StatLabel>
            <StatNumber color="purple.600">
              {summary.totalParticipants}
            </StatNumber>
            <StatHelpText>Total participants</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Last Reaction</StatLabel>
            <StatNumber fontSize="sm" color="gray.600">
              {summary.lastReactionAt
                ? new Date(summary.lastReactionAt).toLocaleDateString()
                : "None"}
            </StatNumber>
            <StatHelpText>Most recent</StatHelpText>
          </Stat>
        </SimpleGrid>
      </Card>

      {/* Reaction Analysis */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Reaction Distribution
        </Text>

        <VStack align="stretch" spacing={3}>
          {reactionAnalysis.map((reaction) => (
            <Card key={reaction.emoji} p={4} borderRadius="lg" bg="gray.50">
              <HStack justify="space-between" mb={2}>
                <HStack>
                  <Text fontSize="2xl">{reaction.emoji}</Text>
                  <Text fontWeight="semibold">Emoji Reaction</Text>
                </HStack>
                <Badge colorScheme="yellow" borderRadius="full">
                  {reaction.count} reaction{reaction.count !== 1 ? "s" : ""} (
                  {reaction.percentage}%)
                </Badge>
              </HStack>
              <Progress
                value={parseFloat(reaction.percentage)}
                colorScheme="yellow"
                borderRadius="full"
                bg="gray.200"
              />
            </Card>
          ))}
        </VStack>
      </Card>

      {/* Recent Reactions */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Recent Reactions ({reactions.length})
        </Text>

        <VStack align="stretch" spacing={3} maxH="400px" overflowY="auto">
          {reactions.slice(0, 10).map((reaction, index) => (
            <Card key={reaction.id} p={4} borderRadius="lg" bg="gray.50">
              <HStack justify="space-between" mb={2}>
                <HStack>
                  <Badge colorScheme="teal" borderRadius="full">
                    #{index + 1}
                  </Badge>
                  <Text fontWeight="semibold">{reaction.member.alias}</Text>
                  {reaction.member.isAnonymous && (
                    <Badge colorScheme="orange" size="sm">
                      Anonymous
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  {new Date(reaction.reactedAt).toLocaleDateString()}
                </Text>
              </HStack>
              <HStack>
                <Text fontSize="lg">{reaction.emoji}</Text>
                <Text fontSize="sm" color="gray.700">
                  Reacted with emoji
                </Text>
              </HStack>
            </Card>
          ))}
        </VStack>
      </Card>
    </VStack>
  );
};

const DetailedFormReport = ({ reportData, onRefresh, loading }) => {
  return (
    <VStack align="stretch" spacing={6}>
      {/* Summary Stats */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <HStack justify="space-between" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="teal.600">
            Form Report
          </Text>
          <Button
            size="sm"
            colorScheme="teal"
            variant="outline"
            borderRadius="md"
            onClick={onRefresh}
            isLoading={loading}
          >
            <FaSyncAlt />
          </Button>
        </HStack>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Stat>
            <StatLabel>
              <FaFileAlt style={{ display: "inline", marginRight: "8px" }} />
              Responses
            </StatLabel>
            <StatNumber color="teal.600">
              {reportData.summary.totalResponses}
            </StatNumber>
            <StatHelpText>Total submissions</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>
              <FaUsers style={{ display: "inline", marginRight: "8px" }} />
              Members
            </StatLabel>
            <StatNumber color="blue.600">
              {reportData.summary.totalParticipants}
            </StatNumber>
            <StatHelpText>Total participants</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>
              <FaChartBar style={{ display: "inline", marginRight: "8px" }} />
              Response Rate
            </StatLabel>
            <StatNumber color="green.600">
              {reportData.summary.responseRate}
            </StatNumber>
            <StatHelpText>Completion rate</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Last Response</StatLabel>
            <StatNumber fontSize="sm" color="gray.600">
              {reportData.summary.lastResponseAt
                ? new Date(
                    reportData.summary.lastResponseAt,
                  ).toLocaleDateString()
                : "None"}
            </StatNumber>
            <StatHelpText>Most recent</StatHelpText>
          </Stat>
        </SimpleGrid>
      </Card>

      {/* Field Analysis */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Field Analysis
        </Text>

        <VStack align="stretch" spacing={4}>
          {Object.entries(reportData.fieldAnalysis).map(([fieldKey, field]) => (
            <FieldAnalysisCard key={fieldKey} field={field} />
          ))}
        </VStack>
      </Card>

      {/* Recent Responses */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Recent Responses ({reportData.responses.length})
        </Text>

        <VStack align="stretch" spacing={3} maxH="400px" overflowY="auto">
          {reportData.responses.slice(0, 10).map((response, index) => (
            <Card key={response.id} p={4} borderRadius="lg" bg="gray.50">
              <HStack justify="space-between" mb={2}>
                <HStack>
                  <Badge colorScheme="teal" borderRadius="full">
                    #{index + 1}
                  </Badge>
                  <Text fontWeight="semibold">{response.member.alias}</Text>
                  {response.member.isAnonymous && (
                    <Badge colorScheme="orange" size="sm">
                      Anonymous
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  {new Date(response.submittedAt).toLocaleDateString()}
                </Text>
              </HStack>
              <Text fontSize="sm" color="gray.700">
                {Object.keys(response.responses).length} field
                {Object.keys(response.responses).length !== 1 ? "s" : ""}{" "}
                completed
              </Text>
            </Card>
          ))}
        </VStack>
      </Card>
    </VStack>
  );
};

const FieldAnalysisCard = ({ field }) => {
  const renderFieldSpecific = () => {
    if (field.fieldType === "ratings" && field.averageRating) {
      return (
        <HStack spacing={4}>
          <Text fontSize="sm">
            <strong>Average Rating:</strong> {field.averageRating}/{field.scale}
          </Text>
          <Progress
            value={(parseFloat(field.averageRating) / field.scale) * 100}
            colorScheme="yellow"
            size="sm"
            width="100px"
            borderRadius="full"
          />
        </HStack>
      );
    }

    if (
      field.choiceDistribution &&
      Object.keys(field.choiceDistribution).length > 0
    ) {
      // Create a mapping from choice IDs to labels
      const choiceMap = {};
      if (field.choices && Array.isArray(field.choices)) {
        field.choices.forEach((choice) => {
          if (choice.id && choice.label) {
            choiceMap[choice.id] = choice.label;
          } else if (typeof choice === "string") {
            choiceMap[choice] = choice;
          }
        });
      }

      return (
        <VStack align="stretch" spacing={3}>
          <Text fontSize="sm" fontWeight="semibold">
            Choice Distribution:
          </Text>
          <Card
            p={3}
            borderRadius="md"
            border="1px"
            borderColor="gray.300"
            bg="white"
          >
            <VStack align="stretch" spacing={2}>
              {Object.entries(field.choiceDistribution).map(
                ([choiceId, count]) => {
                  // Get the label from the choice map, fallback to the ID if not found
                  const choiceLabel = choiceMap[choiceId] || choiceId;

                  return (
                    <HStack
                      key={choiceId}
                      justify="space-between"
                      p={2}
                      borderRadius="sm"
                      bg="gray.50"
                    >
                      <Text fontSize="sm" fontWeight="medium">
                        {choiceLabel}
                      </Text>
                      <Badge colorScheme="teal" borderRadius="full">
                        {count} response{count !== 1 ? "s" : ""}
                      </Badge>
                    </HStack>
                  );
                },
              )}
            </VStack>
          </Card>
        </VStack>
      );
    }

    return null;
  };

  return (
    <Card p={4} borderRadius="lg" bg="gray.50">
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between" align="center">
          <HStack align="center">
            <Text fontWeight="bold">{field.fieldLabel}</Text>
            {field.required && (
              <FaAsterisk size={10} color="red" title="Required field" />
            )}
          </HStack>
          <Badge colorScheme="blue" borderRadius="full">
            {field.fieldType}
          </Badge>
        </HStack>

        <HStack justify="space-between">
          <Text fontSize="sm" color="gray.600">
            Responses: {field.totalResponses}
          </Text>
          <Progress
            value={field.totalResponses > 0 ? 100 : 0}
            colorScheme="teal"
            size="sm"
            width="100px"
            borderRadius="full"
          />
        </HStack>

        {renderFieldSpecific()}
      </VStack>
    </Card>
  );
};

const ChatReportDetailed = ({ reportData, onRefresh, loading }) => {
  const {
    memberStatistics,
    messageStatistics,
    engagementMetrics,
    spaceActivityStatus,
    recentActivity,
    charts,
  } = reportData;

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header and Summary Stats */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <HStack justify="space-between" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="teal.600">
            Chat Analytics Report
          </Text>
          <Button
            size="sm"
            colorScheme="teal"
            variant="outline"
            borderRadius="md"
            onClick={onRefresh}
            isLoading={loading}
            leftIcon={<FaSyncAlt />}
          >
            Refresh
          </Button>
        </HStack>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Stat>
            <StatLabel>
              <FaComments style={{ display: "inline", marginRight: "8px" }} />
              Total Messages
            </StatLabel>
            <StatNumber color="teal.600">
              {messageStatistics.totalMessages}
            </StatNumber>
            <StatHelpText>All chat messages</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>
              <FaUsers style={{ display: "inline", marginRight: "8px" }} />
              Active Members
            </StatLabel>
            <StatNumber color="blue.600">
              {memberStatistics.activeMembers}
            </StatNumber>
            <StatHelpText>
              of {memberStatistics.totalMembers} total
            </StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>
              <FaReply style={{ display: "inline", marginRight: "8px" }} />
              Reply Rate
            </StatLabel>
            <StatNumber color="green.600">
              {engagementMetrics.replyRate}%
            </StatNumber>
            <StatHelpText>Message replies</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>
              <FaArrowUp style={{ display: "inline", marginRight: "8px" }} />
              Participation
            </StatLabel>
            <StatNumber color="purple.600">
              {engagementMetrics.participationRate}%
            </StatNumber>
            <StatHelpText>Member activity</StatHelpText>
          </Stat>
        </SimpleGrid>
      </Card>

      {/* Engagement Metrics */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Engagement Overview
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          <VStack align="stretch" spacing={3}>
            <Text fontWeight="semibold" color="gray.600">
              Message Activity
            </Text>
            <Stat>
              <StatLabel>Avg Msgs/Member</StatLabel>
              <StatNumber fontSize="lg">
                {engagementMetrics.averageMessagesPerMember}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Unique Senders</StatLabel>
              <StatNumber fontSize="lg">
                {messageStatistics.uniqueSendersCount}
              </StatNumber>
            </Stat>
          </VStack>

          <VStack align="stretch" spacing={3}>
            <Text fontWeight="semibold" color="gray.600">
              Message Quality
            </Text>
            <Stat>
              <StatLabel>Avg Length</StatLabel>
              <StatNumber fontSize="lg">
                {engagementMetrics.averageMessageLength} chars
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Avg Words</StatLabel>
              <StatNumber fontSize="lg">
                {engagementMetrics.averageWordCount}
              </StatNumber>
            </Stat>
          </VStack>

          <VStack align="stretch" spacing={3}>
            <Text fontWeight="semibold" color="gray.600">
              Response Time
            </Text>
            <Stat>
              <StatLabel>
                <FaClock style={{ display: "inline", marginRight: "8px" }} />
                Avg Response
              </StatLabel>
              <StatNumber fontSize="lg">
                {engagementMetrics.averageResponseTimeMinutes}m
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Space Age</StatLabel>
              <StatNumber fontSize="lg">
                {spaceActivityStatus.daysSinceCreation} days
              </StatNumber>
            </Stat>
          </VStack>
        </SimpleGrid>
      </Card>

      {/* Recent Activity */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Recent Activity (Last 7 Days)
        </Text>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Stat>
            <StatLabel>Messages</StatLabel>
            <StatNumber color="teal.600">
              {recentActivity.messagesLast7Days}
            </StatNumber>
            <StatHelpText>in the last week</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Active Members</StatLabel>
            <StatNumber color="blue.600">
              {recentActivity.activeMembersLast7DaysCount}
            </StatNumber>
            <StatHelpText>participated recently</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Replies</StatLabel>
            <StatNumber color="green.600">
              {recentActivity.repliesLast7Days}
            </StatNumber>
            <StatHelpText>conversation threads</StatHelpText>
          </Stat>
        </SimpleGrid>
      </Card>

      {/* Member Breakdown */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Member Demographics
        </Text>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <VStack align="center" spacing={2}>
            <Text fontWeight="semibold" color="gray.600">
              Total
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="teal.600">
              {memberStatistics.totalMembers}
            </Text>
            <Progress
              value={100}
              colorScheme="teal"
              size="sm"
              width="80px"
              borderRadius="full"
            />
          </VStack>

          <VStack align="center" spacing={2}>
            <Text fontWeight="semibold" color="gray.600">
              Active
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.600">
              {memberStatistics.activeMembers}
            </Text>
            <Progress
              value={
                (memberStatistics.activeMembers /
                  memberStatistics.totalMembers) *
                100
              }
              colorScheme="green"
              size="sm"
              width="80px"
              borderRadius="full"
            />
          </VStack>

          <VStack align="center" spacing={2}>
            <Text fontWeight="semibold" color="gray.600">
              Anonymous
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="orange.600">
              {memberStatistics.anonymousMembers}
            </Text>
            <Progress
              value={
                (memberStatistics.anonymousMembers /
                  memberStatistics.totalMembers) *
                100
              }
              colorScheme="orange"
              size="sm"
              width="80px"
              borderRadius="full"
            />
          </VStack>

          <VStack align="center" spacing={2}>
            <Text fontWeight="semibold" color="gray.600">
              Authenticated
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              {memberStatistics.authenticatedMembers}
            </Text>
            <Progress
              value={
                (memberStatistics.authenticatedMembers /
                  memberStatistics.totalMembers) *
                100
              }
              colorScheme="blue"
              size="sm"
              width="80px"
              borderRadius="full"
            />
          </VStack>
        </SimpleGrid>
      </Card>

      {/* Most Active Members */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Most Active Members
        </Text>

        <VStack align="stretch" spacing={3} maxH="400px" overflowY="auto">
          {charts.mostActiveMembers.slice(0, 10).map((member, index) => (
            <Card key={index} p={4} borderRadius="lg" bg="gray.50">
              <HStack justify="space-between" align="center">
                <HStack spacing={3}>
                  <Badge colorScheme="teal" borderRadius="full">
                    #{index + 1}
                  </Badge>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="semibold">{member.displayName}</Text>
                    <HStack spacing={2}>
                      {member.isOwner && (
                        <Badge colorScheme="purple" size="sm">
                          Owner
                        </Badge>
                      )}
                      {member.isAnonymous && (
                        <Badge colorScheme="orange" size="sm">
                          Anonymous
                        </Badge>
                      )}
                    </HStack>
                  </VStack>
                </HStack>

                <VStack align="end" spacing={1}>
                  <HStack spacing={4}>
                    <VStack align="center" spacing={0}>
                      <Text fontSize="lg" fontWeight="bold" color="teal.600">
                        {member.messageCount}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Messages
                      </Text>
                    </VStack>
                    <VStack align="center" spacing={0}>
                      <Text fontSize="lg" fontWeight="bold" color="green.600">
                        {member.repliesCount}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Replies
                      </Text>
                    </VStack>
                    <VStack align="center" spacing={0}>
                      <Text fontSize="lg" fontWeight="bold" color="purple.600">
                        {member.engagementScore}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Score
                      </Text>
                    </VStack>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    Last: {new Date(member.lastActivity).toLocaleDateString()}
                  </Text>
                </VStack>
              </HStack>
            </Card>
          ))}
        </VStack>
      </Card>

      {/* Peak Activity Hours */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Peak Activity Hours
        </Text>

        <VStack align="stretch" spacing={3}>
          {charts.peakHours.slice(0, 5).map((hourData) => (
            <Card key={hourData.hour} p={3} borderRadius="lg" bg="gray.50">
              <HStack justify="space-between" align="center">
                <HStack spacing={3}>
                  <Badge colorScheme="blue" borderRadius="full" px={3}>
                    {hourData.timeLabel}
                  </Badge>
                  <Text fontWeight="semibold">
                    {hourData.messageCount} message
                    {hourData.messageCount !== 1 ? "s" : ""}
                  </Text>
                </HStack>
                <Progress
                  value={
                    (hourData.messageCount /
                      charts.peakHours[0]?.messageCount) *
                    100
                  }
                  colorScheme="blue"
                  size="sm"
                  width="150px"
                  borderRadius="full"
                />
              </HStack>
            </Card>
          ))}
        </VStack>
      </Card>

      {/* Space Health Status */}
      <Card p={6} borderRadius="xl" boxShadow="md">
        <Text fontSize="lg" fontWeight="bold" mb={4} color="teal.600">
          Space Health
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text fontWeight="semibold">Space Status</Text>
              <Badge
                colorScheme={spaceActivityStatus.isActive ? "green" : "red"}
                borderRadius="full"
              >
                {spaceActivityStatus.isActive ? "Active" : "Inactive"}
              </Badge>
            </HStack>

            <HStack justify="space-between">
              <Text fontWeight="semibold">Days Active</Text>
              <Text fontWeight="bold" color="teal.600">
                {spaceActivityStatus.daysSinceCreation}
              </Text>
            </HStack>

            {spaceActivityStatus.daysSinceLastMessage !== null && (
              <HStack justify="space-between">
                <Text fontWeight="semibold">Last Msg</Text>
                <Text fontWeight="bold" color="blue.600">
                  {spaceActivityStatus.daysSinceLastMessage} days ago
                </Text>
              </HStack>
            )}
          </VStack>

          <VStack align="stretch" spacing={3}>
            {spaceActivityStatus.daysUntilExpiry && (
              <>
                <HStack justify="space-between">
                  <Text fontWeight="semibold">Expires In</Text>
                  <Badge
                    colorScheme={
                      spaceActivityStatus.daysUntilExpiry > 7
                        ? "green"
                        : "orange"
                    }
                    borderRadius="full"
                  >
                    {spaceActivityStatus.daysUntilExpiry} days
                  </Badge>
                </HStack>

                <HStack justify="space-between">
                  <Text fontWeight="semibold">Expiry Status</Text>
                  <Badge
                    colorScheme={
                      spaceActivityStatus.isExpired ? "red" : "green"
                    }
                    borderRadius="full"
                  >
                    {spaceActivityStatus.isExpired ? "Expired" : "Valid"}
                  </Badge>
                </HStack>
              </>
            )}

            <HStack justify="space-between">
              <Text fontWeight="semibold">Total Messages</Text>
              <Text fontWeight="bold" color="purple.600">
                {messageStatistics.totalMessages}
              </Text>
            </HStack>
          </VStack>
        </SimpleGrid>
      </Card>
    </VStack>
  );
};

export default ReportTab;
